import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

const NEW_RELIC_SOURCE = 'ErrorBoundary';

const isRenderFallback = (
  fallback: ErrorBoundaryFallback
): fallback is (props: ErrorBoundaryFallbackProps) => ReactNode => typeof fallback === 'function';

type ErrorBoundaryFallbackProps = {
  error: Error;
  reset: () => void;
};

type ErrorBoundaryFallback = ReactNode | ((props: ErrorBoundaryFallbackProps) => ReactNode);

export type ErrorBoundaryProps = {
  children: ReactNode;
  fallback: ErrorBoundaryFallback;
  onError?: (error: Error, errorInfo: ErrorInfo, reset: () => void) => void;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  error: Error | null;
};

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${NEW_RELIC_SOURCE}] Uncaught error:`, error, errorInfo);

    if (typeof window === 'undefined') {
      throw error;
    }

    this.props.onError?.(error, errorInfo, this.reset);

    window.newrelic?.noticeError(error, {
      source: NEW_RELIC_SOURCE,
      componentStack: errorInfo.componentStack ?? 'unknown',
    });
  }

  private readonly reset = (): void => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    if (error === null) {
      return this.props.children;
    }

    const { fallback } = this.props;
    return isRenderFallback(fallback) ? fallback({ error, reset: this.reset }) : fallback;
  }
}
